import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaRobot } from 'react-icons/fa';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 100px 2rem 2rem 2rem;
  background: #ffffff;
  
  @media (max-width: 768px) {
    padding: 80px 1rem 2rem 1rem;
    min-height: auto;
  }
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0.03;
  background-image: 
    linear-gradient(30deg, #0891b2 12%, transparent 12.5%, transparent 87%, #0891b2 87.5%, #0891b2),
    linear-gradient(150deg, #0891b2 12%, transparent 12.5%, transparent 87%, #0891b2 87.5%, #0891b2),
    linear-gradient(30deg, #0891b2 12%, transparent 12.5%, transparent 87%, #0891b2 87.5%, #0891b2),
    linear-gradient(150deg, #0891b2 12%, transparent 12.5%, transparent 87%, #0891b2 87.5%, #0891b2);
  background-size: 80px 140px;
  background-position: 0 0, 0 0, 40px 70px, 40px 70px;
`;

const FloatingCircle = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
  opacity: 0.05;
`;

const FloatingSquare = styled(motion.div)`
  position: absolute;
  background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
  opacity: 0.03;
  transform: rotate(45deg);
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  z-index: 1;
  position: relative;
`;

const TagLine = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(8, 145, 178, 0.1);
  border: 2px solid #0891b2;
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  color: #0891b2;
  font-weight: 600;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #1a1a1a 0%, #0891b2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: clamp(2rem, 6vw, 2.5rem);
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  color: #4a5568;
  margin-bottom: 3rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const CTAContainer = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
    margin-bottom: 3rem;
  }
`;

const PrimaryButton = styled(motion.button)`
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(8, 145, 178, 0.2);
  
  @media (max-width: 768px) {
    padding: 0.875rem 2rem;
    font-size: 1rem;
    width: 100%;
    max-width: 300px;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(8, 145, 178, 0.3);
  }
`;

const SecondaryButton = styled(motion.button)`
  background: transparent;
  color: #1a1a1a;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  border: 2px solid #1a1a1a;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 0.875rem 2rem;
    font-size: 1rem;
    width: 100%;
    max-width: 300px;
  }
  
  &:hover {
    background: #1a1a1a;
    color: white;
  }
`;

const StatsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    gap: 1rem;
    grid-template-columns: 1fr;
    max-width: 400px;
  }
`;

const StatBox = styled(motion.div)`
  background: #ffffff;
  border: 2px solid #f0f0f0;
  padding: 1.5rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    border-color: #ff6b6b;
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: #0891b2;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 500;
`;

const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <HeroSection>
      <HeroBackground>
        <BackgroundPattern />
        
        <FloatingCircle
          style={{ 
            width: '300px', 
            height: '300px', 
            top: '10%', 
            left: '-5%' 
          }}
          animate={{ 
            x: [0, 30, 0],
            y: [0, -20, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <FloatingCircle
          style={{ 
            width: '200px', 
            height: '200px', 
            bottom: '20%', 
            right: '-3%' 
          }}
          animate={{ 
            x: [0, -20, 0],
            y: [0, 30, 0]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <FloatingSquare
          style={{ 
            width: '150px', 
            height: '150px', 
            top: '50%', 
            right: '10%' 
          }}
          animate={{ 
            rotate: [45, 90, 45],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <FloatingSquare
          style={{ 
            width: '100px', 
            height: '100px', 
            bottom: '10%', 
            left: '15%' 
          }}
          animate={{ 
            rotate: [45, 0, 45],
            scale: [1, 0.9, 1]
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </HeroBackground>
      
      <HeroContent>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <TagLine variants={itemVariants}>
            <FaRobot />
            AI-Powered Player Lifecycle Management for iGaming
          </TagLine>
          
          <HeroTitle variants={itemVariants}>
            Transform Every Player Journey with Intelligent AI Engagement
          </HeroTitle>
          
          <HeroSubtitle variants={itemVariants}>
            From acquisition to retention, Faroflow's AI platform delivers personalized player experiences 
            across the entire lifecycle—boosting conversions, engagement, and lifetime value.
          </HeroSubtitle>
          
          <CTAContainer variants={itemVariants}>
            <PrimaryButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Get Started
            </PrimaryButton>
            <SecondaryButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('use-cases').scrollIntoView({ behavior: 'smooth' })}
            >
              See It In Action
            </SecondaryButton>
          </CTAContainer>
          
          <StatsContainer variants={itemVariants}>
            <StatBox
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <StatNumber>3x</StatNumber>
              <StatLabel>Higher Conversion Rate</StatLabel>
            </StatBox>
            <StatBox
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <StatNumber>65%</StatNumber>
              <StatLabel>Increased Player LTV</StatLabel>
            </StatBox>
            <StatBox
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <StatNumber>24/7</StatNumber>
              <StatLabel>Automated Engagement</StatLabel>
            </StatBox>
          </StatsContainer>
        </motion.div>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero; 